import React, { useState, useEffect, useRef, useMemo } from "react";
import { observer } from "mobx-react-lite";
import {
    Box,
    Typography,
    Paper,
    Avatar,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import CodeIcon from "@mui/icons-material/Code";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SettingsIcon from "@mui/icons-material/Settings";
import FunctionsIcon from "@mui/icons-material/Functions";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ErrorIcon from "@mui/icons-material/Error";
import BugReportIcon from "@mui/icons-material/BugReport";
import StorageIcon from "@mui/icons-material/Storage";
import moment from "moment";
import { ChatSession } from "../../../../../services/AcxAgentService";
import Markdown from "markdown-to-jsx";
import FunctionCallsModal from "./FunctionCallsModal";
import { FunctionCallsArray, AdditionalInfo } from "./types";
import AcxButton from "../../../../UI/AcxButton";
import AcxChip from "../../../../UI/AcxChip";
import AcxEmptyState from "../../../../UI/AcxEmptyStateIndicator/AcxEmptyState";
import { Summarize } from "@mui/icons-material";
import { AgentArtifacts } from "components/KBo/Conversation/MessageGroup/AgentArtifacts";

type Props = {
    organizationId: string;
    chatSession: ChatSession | null;
    onFeedbackToggle: () => void;
    showFeedback: boolean;
};

const parseAdditionalInfo = (
    additionalInfo: string | undefined,
): AdditionalInfo | null => {
    if (!additionalInfo) return null;
    try {
        return JSON.parse(additionalInfo) as AdditionalInfo;
    } catch (e) {
        console.error("Failed to parse additional info:", e);
        return null;
    }
};

type ExtractedXMLTag = {
    tagName: string;
    content: string;
};

const extractXMLTags = (message: string): { tags: ExtractedXMLTag[], remainingMessage: string } => {
    const xmlTagPattern = /^<([a-zA-Z_][a-zA-Z0-9_-]*)>([\s\S]*?)<\/\1>/;
    const tags: ExtractedXMLTag[] = [];
    let remainingMessage = message;
    
    // Keep extracting XML tags from the start of the message
    while (remainingMessage) {
        const match = remainingMessage.match(xmlTagPattern);
        if (!match) break;
        
        tags.push({
            tagName: match[1],
            content: match[2].trim()
        });
        
        // Remove the matched tag from the message
        remainingMessage = remainingMessage.substring(match[0].length).trim();
    }
    
    return { tags, remainingMessage };
};

const XMLTagBlock: React.FC<{ tagName: string; content: string }> = ({ tagName, content }) => {
    // Format tag name: replace underscores with spaces and capitalize
    const formattedTagName = tagName.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
    
    return (
        <Box 
            sx={{ 
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'grey.50',
                p: 2,
                mb: 2,
                ml: 1
            }}
        >
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {formattedTagName}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                <Markdown>
                    {content}
                </Markdown>
            </Typography>
        </Box>
    );
};

const MessageWithXMLTags: React.FC<{ message: string }> = ({ message }) => {
    const { tags, remainingMessage } = extractXMLTags(message);
    
    // If no XML tags, just render the message normally
    if (tags.length === 0) {
        return <Markdown>{message}</Markdown>;
    }
    
    return (
        <>
            {tags.map((tag, index) => {
                // If the tag is user_input, render it normally without the block
                if (tag.tagName === 'user_input') {
                    return (
                        <Box key={index} sx={{ mb: remainingMessage ? 2 : 0 }}>
                            <Markdown>{tag.content}</Markdown>
                        </Box>
                    );
                }
                
                // For other tags, render them in the styled block
                return (
                    <XMLTagBlock 
                        key={index} 
                        tagName={tag.tagName} 
                        content={tag.content} 
                    />
                );
            })}
            {remainingMessage && (
                <Markdown>{remainingMessage}</Markdown>
            )}
        </>
    );
};

const ChatHistoryDetail: React.FC<Props> = observer(
    ({ organizationId, chatSession, onFeedbackToggle, showFeedback }) => {
        const [functionCallsModalOpen, setFunctionCallsModalOpen] =
            useState(false);
        const [selectedFunctionCalls, setSelectedFunctionCalls] = useState<
            FunctionCallsArray | undefined
        >(undefined);
        const [linkCopied, setLinkCopied] = useState(false);
        const [hoveredMessageIndex, setHoveredMessageIndex] = useState<number | null>(null);
        const [testCopied, setTestCopied] = useState(false);
        const [agentStateDialogOpen, setAgentStateDialogOpen] = useState(false);
        const [selectedAgentState, setSelectedAgentState] = useState<any>(null);
        const messagesContainerRef = useRef<HTMLDivElement>(null);
        
        const toolsUsed = useMemo(() => {
            if (!chatSession || !chatSession.messages) {
                return [];
            }
            const tools = new Map<string, number>();

            chatSession.messages.forEach((message) => {
                const additionalInfo = parseAdditionalInfo(
                    message.additionalInformation,
                );
                if (
                    additionalInfo?.FunctionCalls &&
                    Array.isArray(additionalInfo.FunctionCalls)
                ) {
                    additionalInfo.FunctionCalls.forEach((call) => {
                        const toolName =
                            call.Function?.Metadata?.Name ||
                            call.Function?.Name ||
                            "Unknown Tool";
                        tools.set(toolName, (tools.get(toolName) || 0) + 1);
                    });
                }
            });

            return Array.from(tools.entries()).map(([name, count]) => ({
                name,
                count,
            }));
        }, [chatSession]);

        const handleShowFunctionCalls = (
            functionCalls: FunctionCallsArray | undefined,
        ) => {
            setSelectedFunctionCalls(functionCalls);
            setFunctionCallsModalOpen(true);
        };

        const handleCopyLink = () => {
            if (chatSession) {
                const origin = window.location.origin;
                const chatUrl = `${origin}/admin/organizations/${organizationId}/chat-review/${chatSession.id}`;

                navigator.clipboard.writeText(chatUrl).then(() => {
                    setLinkCopied(true);
                    setTimeout(() => setLinkCopied(false), 2000);
                });
            }
        };

        const handleCopyTestCase = (endIndex: number) => {
            if (!chatSession || !chatSession.messages) return;
            
            const messagesToCopy = chatSession.messages.slice(0, endIndex + 1);
            
            const testCase = `[Fact]
public async Task Test()
{
    var messages = new[]
    {
${messagesToCopy.map((msg, index) => {
    const roleMap: { [key: string]: string } = {
        'User': 'AuthorRole.User',
        'Assistant': 'AuthorRole.Assistant',
        'System': 'AuthorRole.System'
    };
    const role = roleMap[msg.messageType] || 'AuthorRole.User';
    
    const messageContent = msg.message
        .replace(/\\/g, '\\\\')
        .replace(/"""/g, '\\"\\"\\"');
    
    const indentation = '        ';
    const messageIndentation = Array(36).fill(' ').join('');
    
    return `${indentation}new ChatMessageContent(${role}, """
${messageIndentation}${messageContent}
${messageIndentation}""")${index < messagesToCopy.length - 1 ? ',' : ''}`;
}).join('\n\n')}
    };

    var result = await GetChatResponseAsync(messages);
    // Add your assertions here
}`; 

            navigator.clipboard.writeText(testCase).then(() => {
                setTestCopied(true);
                setTimeout(() => setTestCopied(false), 2000);
            });
        };

        // Scroll to top when a new chat is selected
        useEffect(() => {
            if (messagesContainerRef.current && chatSession) {
                messagesContainerRef.current.scrollTop = 0;
            }
        }, [chatSession]);

        if (!chatSession) {
            return (
                <AcxEmptyState
                    size="large"
                    variant="empty"
                    overrideTitle="No Chat Selected"
                    overrideSubTitle="Select a chat from the list to view its history"
                    height={400}
                />
            );
        }

        if (!chatSession.messages || chatSession.messages.length === 0) {
            return (
                <AcxEmptyState
                    size="large"
                    variant="empty"
                    overrideTitle="No Messages"
                    overrideSubTitle="No messages found in this chat session"
                    height={400}
                />
            );
        }

        return (
            <Box
                sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                        }}
                    >
                        <Typography variant="h6">Chat Details</Typography>
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <AcxButton
                                variant="outlined"
                                startIcon={
                                    linkCopied ? (
                                        <CheckIcon />
                                    ) : (
                                        <ContentCopyIcon />
                                    )
                                }
                                onClick={handleCopyLink}
                                size="small"
                                color={linkCopied ? "primary" : "primary"}
                            >
                                {linkCopied ? "Copied!" : "Copy Link"}
                            </AcxButton>
                        </Box>
                    </Box>

                    <Box sx={{ display: "flex", gap: 3, mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            <strong>User:</strong> {chatSession.firstName}{" "}
                            {chatSession.lastName} ({chatSession.username})
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <strong>ID:</strong> {chatSession.id}
                        </Typography>
                    </Box>

                    {/* Tool chips */}
                    {toolsUsed.length > 0 && (
                        <Box
                            sx={{
                                display: "flex",
                                gap: 1,
                                mt: 1.5,
                                flexWrap: "wrap",
                                alignItems: "center",
                            }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mr: 1 }}
                            >
                                Tools used:
                            </Typography>
                            {toolsUsed.map(({ name, count }) => (
                                <AcxChip
                                    key={name}
                                    label={
                                        count > 1 ? `${name} (${count}x)` : name
                                    }
                                    size="small"
                                    color="lightBlue"
                                    customMaterialIcon={<FunctionsIcon />}
                                    style={{
                                        borderStyle: "dashed",
                                    }}
                                />
                            ))}
                        </Box>
                    )}
                </Box>

                <Box
                    ref={messagesContainerRef}
                    sx={{ flex: 1, overflow: "auto", p: 2 }}
                >
                    {chatSession.messages.map((message, index) => {
                        const additionalInfo = parseAdditionalInfo(
                            message.additionalInformation,
                        );
                        const artifacts =
                            message.messageType === "Assistant"
                                ? (additionalInfo?.Artifacts ?? [])
                                : [];

                        // Handle system messages as collapsible
                        if (message.messageType === "System") {
                            return (
                                <Box key={message.id} sx={{ mb: 2 }}>
                                    <Accordion defaultExpanded={false}>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{ backgroundColor: "grey.50" }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                }}
                                            >
                                                <Avatar
                                                    sx={{
                                                        width: 24,
                                                        height: 24,
                                                    }}
                                                >
                                                    <SettingsIcon fontSize="small" />
                                                </Avatar>
                                                <Typography variant="subtitle2">
                                                    System Message
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {moment(
                                                        message.createdOn,
                                                    ).format("HH:mm:ss")}
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Paper
                                                sx={{
                                                    p: 1.5,
                                                    backgroundColor: "grey.100",
                                                }}
                                                elevation={0}
                                            >
                                                <Typography variant="body2" component="div">
                                                    <MessageWithXMLTags message={message.message} />
                                                </Typography>
                                            </Paper>
                                        </AccordionDetails>
                                    </Accordion>
                                    {index <
                                        chatSession.messages!.length - 1 && (
                                        <Divider sx={{ my: 1.5 }} />
                                    )}
                                </Box>
                            );
                        }

                        // Regular message rendering for User and Assistant messages
                        return (
                            <Box 
                                key={message.id} 
                                sx={{ 
                                    mb: 2,
                                    position: 'relative',
                                    '&:hover': {
                                        '& .copy-test-button': {
                                            opacity: 1
                                        }
                                    }
                                }}
                                onMouseEnter={() => setHoveredMessageIndex(index)}
                                onMouseLeave={() => setHoveredMessageIndex(null)}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 1,
                                        alignItems: "flex-start",
                                        mb: 0.5,
                                    }}
                                >
                                    <Avatar sx={{ width: 32, height: 32 }}>
                                        {message.messageType === "User" ? (
                                            <PersonIcon />
                                        ) : (
                                            <SmartToyIcon />
                                        )}
                                    </Avatar>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                alignItems: "center",
                                                gap: 1,
                                                mb: 0.5,
                                            }}
                                        >
                                            <Typography variant="subtitle2">
                                                {message.messageType === "User"
                                                    ? chatSession.firstName +
                                                      " " +
                                                      chatSession.lastName
                                                    : "KBo"}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {moment(
                                                    message.createdOn,
                                                ).format("HH:mm:ss")}
                                            </Typography>
                                            {message.messageType ===
                                                "Summary" && (
                                                <AcxChip
                                                    label="Chat History Summarized"
                                                    size="small"
                                                    color="lightBlue"
                                                    customMaterialIcon={
                                                        <Summarize />
                                                    }
                                                />
                                            )}
                                            {additionalInfo?.ConversationsButton && (
                                                <AcxChip
                                                    label="Clicked Button in Conversations"
                                                    size="small"
                                                    color="lightBlue"
                                                    customMaterialIcon={
                                                        <TouchAppIcon />
                                                    }
                                                />
                                            )}
                                            {additionalInfo?.Suggestion && (
                                                <AcxChip
                                                    label="Suggestion"
                                                    size="small"
                                                    color="yellow"
                                                    customMaterialIcon={
                                                        <LightbulbIcon />
                                                    }
                                                />
                                            )}
                                            {additionalInfo?.FunctionCalls && (
                                                <>
                                                    {/* Tool chips */}
                                                    {additionalInfo.FunctionCalls.map(
                                                        (call, idx) => {
                                                            const toolName =
                                                                call.Function
                                                                    ?.Metadata
                                                                    ?.Name ||
                                                                call.Function
                                                                    ?.Name ||
                                                                "Unknown Tool";
                                                            const hasException =
                                                                !!call.Exception;
                                                            return (
                                                                <AcxChip
                                                                    key={idx}
                                                                    label={
                                                                        toolName
                                                                    }
                                                                    size="small"
                                                                    color={
                                                                        hasException
                                                                            ? "red"
                                                                            : "lightBlue"
                                                                    }
                                                                    customMaterialIcon={
                                                                        hasException ? (
                                                                            <ErrorIcon />
                                                                        ) : (
                                                                            <FunctionsIcon />
                                                                        )
                                                                    }
                                                                    style={{
                                                                        borderStyle:
                                                                            hasException
                                                                                ? "solid"
                                                                                : "dashed",
                                                                    }}
                                                                />
                                                            );
                                                        },
                                                    )}
                                                    <AcxButton
                                                        size="small"
                                                        startIcon={<CodeIcon />}
                                                        onClick={() =>
                                                            handleShowFunctionCalls(
                                                                additionalInfo.FunctionCalls,
                                                            )
                                                        }
                                                        variant="outlined"
                                                    >
                                                        View Tool Call Details
                                                    </AcxButton>
                                                </>
                                            )}
                                            {additionalInfo?.AgentState && (
                                                <AcxButton
                                                    size="small"
                                                    startIcon={<StorageIcon />}
                                                    onClick={() => {
                                                        setSelectedAgentState(additionalInfo.AgentState);
                                                        setAgentStateDialogOpen(true);
                                                    }}
                                                    variant="outlined"
                                                >
                                                    View Agent State
                                                </AcxButton>
                                            )}
                                            <Tooltip 
                                                title={testCopied && hoveredMessageIndex === index ? "Copied!" : "Copy to XUnit Test Case"}
                                                placement="top"
                                            >
                                                <IconButton
                                                    className="copy-test-button"
                                                    size="small"
                                                    onClick={() => handleCopyTestCase(index)}
                                                    sx={{ 
                                                        opacity: 0,
                                                        transition: 'opacity 0.2s',
                                                        ml: 'auto'
                                                    }}
                                                >
                                                    {testCopied && hoveredMessageIndex === index ? (
                                                        <CheckIcon fontSize="small" />
                                                    ) : (
                                                        <BugReportIcon fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        {/* Show DisplayMessage if present, otherwise show regular message */}
                                        {!!additionalInfo?.ReasoningSummaries?.length && (
                                            <Box component="details" sx={{ my: 1 }}>
                                                <summary>Reasoning summaries ({additionalInfo.ReasoningSummaries.length})</summary>
                                                {additionalInfo.ReasoningSummaries.map((summary, index) => (
                                                    <Typography key={`${summary.timestampUtc}-${index}`} variant="body2" sx={{ whiteSpace: "pre-wrap", my: 1 }}>
                                                        Turn {summary.modelTurn}: {summary.text}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        )}
                                        {additionalInfo?.DisplayMessage && (
                                            <Box sx={{ mb: 1 }}>
                                                <Typography 
                                                    variant="caption" 
                                                    color="text.secondary"
                                                    sx={{ display: 'block', mb: 0.5, ml: 0.5 }}
                                                >
                                                    Message shown to user:
                                                </Typography>
                                                <Paper
                                                    sx={{
                                                        p: 1.5,
                                                        backgroundColor:
                                                            message.messageType ===
                                                            "User"
                                                                ? "grey.100"
                                                                : "primary.50",
                                                        maxWidth: "80%",
                                                    }}
                                                    elevation={0}
                                                >
                                                    <Typography variant="body2" component="div">
                                                        <MessageWithXMLTags message={additionalInfo.DisplayMessage} />
                                                    </Typography>
                                                </Paper>
                                                <Typography 
                                                    variant="caption" 
                                                    color="text.secondary"
                                                    sx={{ display: 'block', mt: 1, mb: 0.5, ml: 0.5 }}
                                                >
                                                    This is what was sent to the LLM:
                                                </Typography>
                                            </Box>
                                        )}
                                        <Paper
                                            sx={{
                                                p: 1.5,
                                                backgroundColor:
                                                    additionalInfo?.DisplayMessage
                                                        ? "grey.50"
                                                        : message.messageType === "User"
                                                        ? "grey.100"
                                                        : "primary.50",
                                                maxWidth: "80%",
                                                border: additionalInfo?.DisplayMessage ? '1px dashed' : 'none',
                                                borderColor: 'divider',
                                            }}
                                            elevation={0}
                                        >
                                            <Typography variant="body2" component="div">
                                                <MessageWithXMLTags message={message.message} />
                                            </Typography>
                                        </Paper>
                                        {artifacts.length > 0 && (
                                            <Box sx={{ mt: 1 }}>
                                                <AgentArtifacts artifacts={artifacts} />
                                            </Box>
                                        )}
                                        {message.userFeedbackType && (
                                            <Box sx={{ mt: 1 }}>
                                                <Paper
                                                    sx={{
                                                        p: 1.5,
                                                        backgroundColor:
                                                            message.userFeedbackType.toLowerCase() ===
                                                            "like"
                                                                ? "success.50"
                                                                : "error.50",
                                                        border: 1,
                                                        borderColor:
                                                            message.userFeedbackType.toLowerCase() ===
                                                            "like"
                                                                ? "success.main"
                                                                : "error.main",
                                                    }}
                                                    elevation={0}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            gap: 1,
                                                            mb: message.userFeedback
                                                                ? 1
                                                                : 0,
                                                        }}
                                                    >
                                                        {message.userFeedbackType.toLowerCase() ===
                                                        "like" ? (
                                                            <>
                                                                <ThumbUpIcon
                                                                    color="success"
                                                                    fontSize="small"
                                                                />
                                                                <Typography
                                                                    variant="body2"
                                                                    color="success.main"
                                                                    fontWeight="medium"
                                                                >
                                                                    User liked
                                                                    this
                                                                    response
                                                                </Typography>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <ThumbDownIcon
                                                                    color="error"
                                                                    fontSize="small"
                                                                />
                                                                <Typography
                                                                    variant="body2"
                                                                    color="error"
                                                                    fontWeight="medium"
                                                                >
                                                                    User gave
                                                                    negative
                                                                    feedback
                                                                </Typography>
                                                            </>
                                                        )}
                                                    </Box>
                                                    {message.userFeedback && (
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ ml: 3 }}
                                                        >
                                                            <strong>
                                                                Feedback:
                                                            </strong>{" "}
                                                            {
                                                                message.userFeedback
                                                            }
                                                        </Typography>
                                                    )}
                                                </Paper>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                                {index < chatSession.messages!.length - 1 && (
                                    <Divider sx={{ my: 1.5, ml: 5 }} />
                                )}
                            </Box>
                        );
                    })}
                </Box>

                <FunctionCallsModal
                    open={functionCallsModalOpen}
                    onClose={() => setFunctionCallsModalOpen(false)}
                    functionCalls={selectedFunctionCalls}
                />
                
                {/* Agent State Dialog */}
                <Dialog
                    open={agentStateDialogOpen}
                    onClose={() => setAgentStateDialogOpen(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>Agent State</DialogTitle>
                    <DialogContent>
                        <Paper
                            sx={{
                                p: 2,
                                backgroundColor: "grey.50",
                                fontFamily: "monospace",
                                fontSize: "0.875rem",
                                overflow: "auto",
                                maxHeight: "60vh",
                            }}
                            elevation={0}
                        >
                            <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                {selectedAgentState && JSON.stringify(selectedAgentState, null, 2)}
                            </pre>
                        </Paper>
                    </DialogContent>
                    <DialogActions>
                        <AcxButton onClick={() => setAgentStateDialogOpen(false)}>
                            Close
                        </AcxButton>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    },
);

export default ChatHistoryDetail;
